/**
 * THE HEADS-UP PAGE REGISTRY — ONE LIST, THREE CONSUMERS, SO A NEW PAGE CANNOT ARRIVE HALF-WIRED.
 *
 * ⛔⛔ THE DEFECT THIS EXISTS FOR, MEASURED ON main 2026-08-24 ~00:3xZ. The same fact — *which
 * heads-up pages exist* — was hand-maintained in THREE places, and every omission was silent and
 * failed DIFFERENTLY:
 * ```
 *   BUILD  vite.headsup.config.ts   input:{slice1,slice2,spike}   miss it -> never emitted
 *   GATE   scripts/check-headsup.mjs HEADSUP=[...3]               miss it -> unchecked
 *   NAV    src/App.svelte            /slice1 /slice2  ** spike:0 ** miss it -> UNREACHABLE
 * ```
 * ⇒ ***`spike` WAS BUILT, GATED, AND SERVING 200 AT 1,204 B WITH NO DOOR*** — live and
 * unreachable, and NOT the SPA fallback. CC-qq measured it while the cockpit-link cure for exactly
 * that disease was being keyed one row away.
 *
 * ⭐ CC-qq NAMED THE CLASS: HUDCHAT-6 diagnosed *"hosted but unreachable"* perfectly and cured the
 * one instance in front of him; `slice2` did not exist yet, so it inherited the disease the cure
 * was written for. I then repeated the shape by hand-adding a second anchor. ⇒ ***A NEGATIVE LIST
 * ARMS THE INSTANCES; ONLY A POSITIVE ONE ARMS THE CLASS.***
 *
 * ⚠ AND THE REFINEMENT THAT STOPS THE CURE BECOMING ITS OWN DEFECT: *"a link per BUILT page"* would
 * put the R389 dev spike on the owner's cockpit. ***BUILT AND OWNER-FACING ARE DIFFERENT
 * PROPERTIES, AND A LIST THAT CONFLATES THEM CURES REACHABILITY BY CREATING CLUTTER.*** So the row
 * carries INTENT: `spike` stays built, stays gated, and stays off the tab row BY DECLARATION rather
 * than by omission — which is the difference between a decision and an oversight, and the only one
 * a later reader can audit.
 *
 * ⛔ WHY `.mjs` AND NOT `.ts`, WHICH IS THE REPO'S CONVENTION — THE FORMAT IS FORCED, NOT CHOSEN:
 * `check:headsup` runs `node scripts/check-headsup.mjs` with NO `--experimental-strip-types`, so
 * that consumer cannot import a `.ts`. The alternative was adding a flag to `package.json` —
 * ***THE EXACT FILE WHOSE MERGE DISARMED THE PIXEL WITNESS FOR 1h45m TONIGHT.*** A registry whose
 * adoption requires touching the gate wiring is not worth its own benefit.
 */

/**
 * @typedef {object} HeadsupPage
 * @property {string}  id            rollup input name — also the built asset's prefix
 * @property {string}  html          the entry HTML at the app root
 * @property {string}  href          the served path
 * @property {string}  label         what the cockpit tab says, when it says anything
 * @property {string}  title         the anchor's title/tooltip
 * @property {boolean} ownerVisible  DECLARED intent: does this belong on the owner's cockpit?
 */

/** @type {readonly HeadsupPage[]} */
export const HEADSUP_PAGES = Object.freeze([
  Object.freeze({
    id: "slice1",
    html: "slice1.html",
    href: "/slice1",
    label: "TV CHART",
    title:
      "ES price + session-anchored CVD on one time axis (TradingView lightweight-charts). " +
      "Heads-up page — opens alongside the cockpit.",
    ownerVisible: true,
  }),
  Object.freeze({
    id: "slice2",
    html: "slice2.html",
    href: "/slice2",
    label: "V-SPLIT",
    title:
      "V-SPLIT — MC Stoch fast against slow, the owner's Pine construct ported bar-for-bar. " +
      "Heads-up page — opens alongside the cockpit.",
    ownerVisible: true,
  }),
  Object.freeze({
    id: "themecheck",
    html: "themecheck.html",
    href: "/themecheck",
    label: "THEME CHECK",
    title: "Both-theme pixel harness for components with no cockpit mounter — gate surface.",
    // ⛔ FALSE, and it is the registry's own distinction doing its job. GK-ad measured that
    // `check-render.mjs` navigates to slice1.html ONLY, so a component with no mounter is NEVER
    // rendered and AGENTS.md law 8's MANDATORY light-AND-dark check had simply never run on it.
    // ⇒ BEING UNMOUNTED IS NOT NEUTRAL; IT IS WHAT MAKES A MANDATED GATE SILENTLY NOT RUN.
    // Built and gated deliberately, kept off the owner's tab row deliberately.
    ownerVisible: false,
  }),
  Object.freeze({
    id: "cvd-compare",
    html: "cvd-compare.html",
    href: "/cvd-compare",
    label: "CVD COMPARE",
    title: "Engineering-only CVD visual treatment comparison.",
    ownerVisible: false,
  }),
  Object.freeze({
    id: "spike",
    html: "spike.html",
    href: "/spike",
    label: "SPIKE",
    title: "R389 rendering spike — engineering surface, not an owner page.",
    // ⛔ FALSE IS A DECISION, NOT AN OVERSIGHT. It is built and gated deliberately; it is kept off
    // the owner's tab row deliberately. Flipping this to true is a UI change and should be argued.
    ownerVisible: false,
  }),
]);

/** BUILD consumer — `rollupOptions.input`. Every page is built, owner-facing or not. */
export function buildInput() {
  return Object.fromEntries(HEADSUP_PAGES.map((p) => [p.id, p.html]));
}

/** GATE consumer — every page is asserted, owner-facing or not. Intent must not narrow coverage. */
export function gatedHtml() {
  return HEADSUP_PAGES.map((p) => p.html);
}

/** NAV consumer — ONLY the pages declared owner-facing get a door on the cockpit. */
export function ownerLinks() {
  return HEADSUP_PAGES.filter((p) => p.ownerVisible);
}
